import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format date to readable string
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Format date with time
export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format relative time
export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return formatDate(dateString);
}

// Format number with thousands separator
export function formatNumber(num) {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('es-ES');
}

// Format points
export function formatPoints(points) {
  return `${formatNumber(points)} pts`;
}

// Calculate progress percentage
export function calculateProgress(current, target) {
  if (!target || target <= 0) return 100;
  const progress = (current / target) * 100;
  return Math.min(100, Math.max(0, progress));
}

// Get level color class
export function getLevelColor(levelName) {
  const name = levelName?.toLowerCase() || '';
  if (name.includes('bronze') || name.includes('bronce')) return 'level-bronze';
  if (name.includes('silver') || name.includes('plata')) return 'level-silver';
  if (name.includes('gold') || name.includes('oro')) return 'level-gold';
  if (name.includes('platinum') || name.includes('platino')) return 'level-platinum';
  if (name.includes('diamond') || name.includes('diamante')) return 'level-diamond';
  return '';
}

// Generate avatar initials
export function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// Validate email
export function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Generate random ID
export function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

// Apply dynamic branding to CSS variables
export function applyBranding(branding) {
  if (!branding) return;
  
  const root = document.documentElement;
  
  if (branding.primary_color) {
    root.style.setProperty('--brand-primary', branding.primary_color);
  }
  if (branding.secondary_color) {
    root.style.setProperty('--brand-secondary', branding.secondary_color);
  }
  if (branding.accent_color) {
    root.style.setProperty('--brand-accent', branding.accent_color);
  }
  if (branding.background_color) {
    root.style.setProperty('--brand-background', branding.background_color);
  }
  if (branding.text_color) {
    root.style.setProperty('--brand-text', branding.text_color);
  }
}

// Reset branding to defaults
export function resetBranding() {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', '#6366F1');
  root.style.setProperty('--brand-secondary', '#8B5CF6');
  root.style.setProperty('--brand-accent', '#F59E0B');
  root.style.setProperty('--brand-background', '#0B0C15');
  root.style.setProperty('--brand-text', '#FAFAFA');
}

// Check if reward is expired
export function isRewardExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

// Get reward status
export function getRewardStatus(redemption) {
  if (!redemption) return 'available';
  if (redemption.status === 'completed') return 'redeemed';
  if (isRewardExpired(redemption.expires_at)) return 'expired';
  return redemption.status;
}

// Storage helpers
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore storage errors
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore storage errors
    }
  },
};

// Theme helpers
export const theme = {
  get: () => storage.get('theme') || 'dark',
  set: (mode) => {
    storage.set('theme', mode);
    if (mode === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  },
  toggle: () => {
    const current = theme.get();
    theme.set(current === 'dark' ? 'light' : 'dark');
    return current === 'dark' ? 'light' : 'dark';
  },
  init: () => {
    const mode = theme.get();
    if (mode === 'light') {
      document.documentElement.classList.add('light');
    }
  },
};
